import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { grantsApi, formatApiError } from '../lib/api';
import { pollJob } from '../lib/pollJob';
import { WriteOnly } from '../components/ViewerGuard';
import usePageTitle from '../hooks/usePageTitle';
import HowDoesThisWorkPill from '../components/HowDoesThisWorkPill';
import { HelpButton } from '../components/HelpPanel';
import { toast } from 'sonner';
import {
  Plus, Radar, Download, Filter, ExternalLink, Star,
  Clock, AlertTriangle, ChevronDown, ChevronUp, X,
  Loader2, FileText, ArrowRight, Check, Copy,
  Globe, Tag, DollarSign, Calendar, User, Mail,
  Eye, Trash2, Edit3, BookOpen, GitBranch
} from 'lucide-react';

const STATUS_OPTIONS = ['Watching', 'Applying', 'Submitted', 'Awarded', 'Missed'];

const URGENCY_CONFIG = {
  URGENT: { label: 'URGENT', bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', dot: 'bg-red-500' },
  CLOSING_SOON: { label: 'CLOSING SOON', bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30', dot: 'bg-orange-500' },
  OPEN_ACT_SOON: { label: 'OPEN — ACT SOON', bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/30', dot: 'bg-yellow-500' },
  OPEN: { label: 'OPEN', bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', dot: 'bg-emerald-500' },
  CLOSED: { label: 'CLOSED', bg: 'bg-neutral-500/15', text: 'text-neutral-400', border: 'border-neutral-500/30', dot: 'bg-neutral-500' },
  UNKNOWN: { label: 'TBD', bg: 'bg-neutral-500/15', text: 'text-neutral-400', border: 'border-neutral-500/30', dot: 'bg-neutral-500' },
};

const STATUS_FILTERS = ['All', 'Open', 'Closing Soon', 'Applied', 'Awarded', 'Closed'];

// ---- Add Grant Modal ----
const AddGrantModal = ({ open, onClose, onSave, editGrant }) => {
  const [form, setForm] = useState({
    name: '', funder_name: '', type: '', website: '', application_url: '',
    amount_min: '', amount_max: '', amount_currency: 'USD', amount_notes: '',
    deadline: '', deadline_type: '', eligibility_criteria: '',
    geographic_focus: '', thematic_focus: '', mandate_summary: '',
    programme_officer_name: '', programme_officer_email: '', notes: '',
  });

  useEffect(() => {
    if (editGrant) {
      setForm({
        name: editGrant.name || '', funder_name: editGrant.funder_name || '',
        type: editGrant.type || '', website: editGrant.website || '',
        application_url: editGrant.application_url || '',
        amount_min: editGrant.amount_min || '', amount_max: editGrant.amount_max || '',
        amount_currency: editGrant.amount_currency || 'USD', amount_notes: editGrant.amount_notes || '',
        deadline: editGrant.deadline || '', deadline_type: editGrant.deadline_type || '',
        eligibility_criteria: editGrant.eligibility_criteria || '',
        geographic_focus: (editGrant.geographic_focus || []).join(', '),
        thematic_focus: (editGrant.thematic_focus || []).join(', '),
        mandate_summary: editGrant.mandate_summary || '',
        programme_officer_name: editGrant.programme_officer_name || '',
        programme_officer_email: editGrant.programme_officer_email || '',
        notes: editGrant.notes || '',
      });
    } else {
      setForm({
        name: '', funder_name: '', type: '', website: '', application_url: '',
        amount_min: '', amount_max: '', amount_currency: 'USD', amount_notes: '',
        deadline: '', deadline_type: '', eligibility_criteria: '',
        geographic_focus: '', thematic_focus: '', mandate_summary: '',
        programme_officer_name: '', programme_officer_email: '', notes: '',
      });
    }
  }, [editGrant, open]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...form,
      amount_min: form.amount_min ? parseFloat(form.amount_min) : null,
      amount_max: form.amount_max ? parseFloat(form.amount_max) : null,
      geographic_focus: form.geographic_focus ? form.geographic_focus.split(',').map(s => s.trim()).filter(Boolean) : [],
      thematic_focus: form.thematic_focus ? form.thematic_focus.split(',').map(s => s.trim()).filter(Boolean) : [],
    };
    onSave(data);
  };

  const inputCls = "w-full p-2.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 placeholder:text-[#A3B5C7]/40";
  const labelCls = "block text-xs uppercase tracking-wider text-[#A3B5C7] mb-1";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#1A2F4C]">
          <h2 className="text-xl font-bold text-white">{editGrant ? 'Edit Grant' : 'Add Grant'}</h2>
          <button onClick={onClose} className="text-[#A3B5C7] hover:text-white" data-testid="close-grant-modal"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Grant / Fund Name *</label><input className={inputCls} value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="grant-name-input" /></div>
            <div><label className={labelCls}>Funder Name</label><input className={inputCls} value={form.funder_name} onChange={e => setForm({...form, funder_name: e.target.value})} data-testid="grant-funder-input" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className={labelCls}>Type</label><input className={inputCls} placeholder="Grant, Fellowship..." value={form.type} onChange={e => setForm({...form, type: e.target.value})} /></div>
            <div><label className={labelCls}>Website</label><input className={inputCls} value={form.website} onChange={e => setForm({...form, website: e.target.value})} /></div>
            <div><label className={labelCls}>Application URL</label><input className={inputCls} value={form.application_url} onChange={e => setForm({...form, application_url: e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div><label className={labelCls}>Amount Min</label><input className={inputCls} type="number" value={form.amount_min} onChange={e => setForm({...form, amount_min: e.target.value})} /></div>
            <div><label className={labelCls}>Amount Max</label><input className={inputCls} type="number" value={form.amount_max} onChange={e => setForm({...form, amount_max: e.target.value})} /></div>
            <div><label className={labelCls}>Currency</label><input className={inputCls} value={form.amount_currency} onChange={e => setForm({...form, amount_currency: e.target.value})} /></div>
            <div><label className={labelCls}>Deadline</label><input className={inputCls} type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} data-testid="grant-deadline-input" /></div>
          </div>
          <div><label className={labelCls}>Mandate Summary</label><textarea className={inputCls + " h-20"} value={form.mandate_summary} onChange={e => setForm({...form, mandate_summary: e.target.value})} /></div>
          <div><label className={labelCls}>Eligibility Criteria</label><textarea className={inputCls + " h-16"} value={form.eligibility_criteria} onChange={e => setForm({...form, eligibility_criteria: e.target.value})} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Geographic Focus (comma-separated)</label><input className={inputCls} placeholder="Latin America, Global South" value={form.geographic_focus} onChange={e => setForm({...form, geographic_focus: e.target.value})} /></div>
            <div><label className={labelCls}>Thematic Focus (comma-separated)</label><input className={inputCls} placeholder="e.g. your thematic focus areas" value={form.thematic_focus} onChange={e => setForm({...form, thematic_focus: e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Programme Officer</label><input className={inputCls} value={form.programme_officer_name} onChange={e => setForm({...form, programme_officer_name: e.target.value})} /></div>
            <div><label className={labelCls}>Officer Email</label><input className={inputCls} type="email" value={form.programme_officer_email} onChange={e => setForm({...form, programme_officer_email: e.target.value})} /></div>
          </div>
          <div><label className={labelCls}>Notes</label><textarea className={inputCls + " h-16"} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          <div className="flex justify-end gap-3 pt-4 border-t border-[#1A2F4C]">
            <button type="button" onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button type="submit" className="btn-primary text-sm" data-testid="save-grant-btn">{editGrant ? 'Update Grant' : 'Add Grant'}</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// ---- Funding Intelligence Scan Modal ----
const SCAN_LOADING_MESSAGES = [
  "Scanning 50 funding sources across 5 categories...",
  "Matching opportunities to your Opportunity Universe...",
  "Scoring fit and checking deadlines...",
  "Identifying CRITICAL and URGENT flags...",
  "Building your TOP 3 ACTIONS THIS WEEK...",
];

const ScanProgressModal = ({ open, scanData, onClose, onSaveApproved }) => {
  const [approved, setApproved] = useState({});
  const [scanMsgIndex, setScanMsgIndex] = useState(0);

  useEffect(() => {
    if (scanData?.candidates?.length) {
      const init = {};
      scanData.candidates.forEach((_, i) => { if (!scanData.candidates[i].is_duplicate) init[i] = true; });
      setApproved(init);
    }
  }, [scanData?.candidates]);

  // Rotate scan loading messages
  useEffect(() => {
    const isScanning = scanData?.status === 'scanning' || scanData?.status === 'processing';
    if (!isScanning) { setScanMsgIndex(0); return; }
    const interval = setInterval(() => {
      setScanMsgIndex((prev) => (prev + 1) % SCAN_LOADING_MESSAGES.length);
    }, 9000);
    return () => clearInterval(interval);
  }, [scanData?.status]);

  if (!open) return null;

  const isReview = scanData?.status === 'review';
  const candidates = scanData?.candidates || [];
  const top3 = scanData?.top_3_actions || [];
  const approvedCount = Object.values(approved).filter(Boolean).length;

  // Group candidates by opportunity_type
  const groupOrder = ['CRITICAL', 'ATTENTION', 'Non-Dilutive Grant', 'Accelerator Programme', 'Competition / Challenge', 'Prize / Award', 'Dilutive Investment', 'Revenue Opportunity', 'Other'];
  const grouped = {};
  candidates.forEach((c, i) => {
    let group = c.opportunity_type || 'Other';
    // Override group if a deadline flag is present (CRITICAL <14d, ATTENTION 14-30d)
    if (c.urgency_flag === 'CRITICAL') group = 'CRITICAL';
    else if (c.urgency_flag === 'ATTENTION' || c.urgency_flag === 'URGENT') group = 'ATTENTION';
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push({ ...c, _idx: i });
  });

  const groupColors = {
    'CRITICAL': { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', dot: 'bg-red-500' },
    'ATTENTION': { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-400' },
    'Non-Dilutive Grant': { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', dot: 'bg-emerald-400' },
    'Accelerator Programme': { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', dot: 'bg-blue-400' },
    'Competition / Challenge': { bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/30', text: 'text-fuchsia-400', dot: 'bg-fuchsia-400' },
    'Prize / Award': { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', dot: 'bg-yellow-400' },
    'Dilutive Investment': { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', dot: 'bg-purple-400' },
    'Revenue Opportunity': { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', dot: 'bg-cyan-400' },
    'Other': { bg: 'bg-neutral-500/10', border: 'border-neutral-500/30', text: 'text-neutral-400', dot: 'bg-neutral-400' },
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#1A2F4C]">
          <div>
            <h2 className="text-xl font-bold text-white" data-testid="scan-modal-title">Funding Intelligence Scan</h2>
            <p className="text-xs text-[#A3B5C7]">
              {scanData?.cached ? (
                <>Cached scan{scanData?.scanned_at ? ` · ${new Date(scanData.scanned_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''} — press Refresh Funding Intelligence for a live update</>
              ) : (
                <>
                  {scanData?.searches_done || 0} / {scanData?.total_searches || 0} searches complete
                  {scanData?.results_count > 0 && ` — ${scanData.results_count} raw results`}
                </>
              )}
            </p>
          </div>
          {isReview && <button onClick={onClose} className="text-[#A3B5C7] hover:text-white"><X className="w-5 h-5" /></button>}
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Rotating status message */}
          {!isReview && (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-[#0A1628] border border-[#1A2F4C]">
                <Loader2 className="w-5 h-5 text-[#00D4C8] animate-spin" />
                <AnimatePresence mode="wait">
                  <motion.span
                    key={scanMsgIndex}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="text-white text-sm"
                  >
                    {SCAN_LOADING_MESSAGES[scanMsgIndex]}
                  </motion.span>
                </AnimatePresence>
              </div>
              <p className="text-xs text-[#A3B5C7]/50">Typically takes 2-3 minutes — scanning across your full Opportunity Universe.</p>
            </div>
          )}

          {/* Live progress log */}
          {!isReview && (
            <div className="bg-[#0A1628] rounded-lg border border-[#1A2F4C] p-4 font-mono text-xs space-y-1 max-h-[400px] overflow-y-auto" data-testid="scan-progress-log">
              {(scanData?.progress_lines || []).map((line, i) => (
                <div key={i} className={`${line.includes('found 0') ? 'text-[#A3B5C7]/50' : line.includes('found') ? 'text-emerald-400' : line.includes('pass') ? 'text-[#00D4C8]' : 'text-[#A3B5C7]'}`}>
                  {line}
                </div>
              ))}
              {scanData?.status === 'scanning' && (
                <div className="flex items-center gap-2 text-[#00D4C8]">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>{scanData?.current_search || 'Searching...'}</span>
                </div>
              )}
              {scanData?.status === 'processing' && (
                <div className="flex items-center gap-2 text-[#00D4C8]">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>AI analysing results across Opportunity Universe...</span>
                </div>
              )}
            </div>
          )}

          {/* TOP 3 ACTIONS THIS WEEK — above everything else */}
          {isReview && top3.length > 0 && (
            <div className="rounded-xl border-2 border-[#00D4C8]/40 bg-[#00D4C8]/5 p-5" data-testid="top-3-actions">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-[#00D4C8]" />
                <h3 className="text-base font-bold text-[#00D4C8] uppercase tracking-wider">Top 3 Actions This Week</h3>
              </div>
              <div className="space-y-3">
                {top3.map((action, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#00D4C8] text-[#0A1628] flex items-center justify-center text-sm font-bold">{i + 1}</div>
                    <div className="flex-1">
                      <p className="text-sm text-white font-medium">{action.action}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {action.deadline && <span className="text-xs text-[#00D4C8] flex items-center gap-1"><Clock className="w-3 h-3" />{action.deadline}</span>}
                        {action.why && <span className="text-xs text-[#A3B5C7]">{action.why}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grouped results */}
          {isReview && (
            <div className="space-y-4" data-testid="scan-review-list">
              {scanData?.banner_note && (
                <div data-testid="scan-banner-note" className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-300">
                  {scanData.banner_note}
                </div>
              )}
              <p className="text-sm text-[#D4E2F0]">
                Found <strong className="text-[#00D4C8]">{candidates.length}</strong> funding opportunities across your Opportunity Universe. Select which to save:
              </p>
              {groupOrder.filter(g => grouped[g]?.length).map(groupName => {
                const gc = groupColors[groupName] || groupColors['Other'];
                const items = grouped[groupName];
                return (
                  <div key={groupName}>
                    <div className={`flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg ${gc.bg} border ${gc.border}`}>
                      <span className={`w-2 h-2 rounded-full ${gc.dot}`} />
                      <span className={`text-xs font-bold uppercase tracking-wider ${gc.text}`}>{groupName}</span>
                      <span className={`text-xs ${gc.text} opacity-60`}>({items.length})</span>
                    </div>
                    <div className="space-y-2 ml-1">
                      {items.map((c) => (
                        <div key={c._idx} className={`p-4 rounded-lg border ${approved[c._idx] ? 'bg-[#0A1628] border-[#1A2F4C]' : 'bg-[#0A1628]/50 border-[#1A2F4C]/50 opacity-50'} ${c.is_duplicate ? 'opacity-30' : ''}`}>
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={!!approved[c._idx]}
                              onChange={() => setApproved(prev => ({...prev, [c._idx]: !prev[c._idx]}))}
                              className="mt-1 accent-[#00D4C8]"
                              disabled={c.is_duplicate}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-white text-sm font-medium">{c.name}</span>
                                {c.funder_name && <span className="text-xs text-[#A3B5C7]">by {c.funder_name}</span>}
                                {c.fit_score && (
                                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${
                                    c.fit_score >= 8 ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' :
                                    c.fit_score >= 5 ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' :
                                    'bg-neutral-500/15 text-neutral-400 border border-neutral-500/20'
                                  }`}>FIT {c.fit_score}/10</span>
                                )}
                                {c.geographic_eligibility && <span className="text-[10px] px-2 py-0.5 bg-blue-500/10 text-blue-400/80 rounded border border-blue-500/15">{c.geographic_eligibility}</span>}
                                {c.urgency_flag === 'CRITICAL' && <span className="text-[10px] px-2 py-0.5 bg-red-500/15 text-red-400 rounded border border-red-500/30 font-bold">CRITICAL{typeof c.days_remaining === 'number' ? ` · ${c.days_remaining}d` : ''}</span>}
                                {c.urgency_flag === 'ATTENTION' && <span className="text-[10px] px-2 py-0.5 bg-amber-500/15 text-amber-400 rounded border border-amber-500/30 font-bold">ATTENTION{typeof c.days_remaining === 'number' ? ` · ${c.days_remaining}d` : ''}</span>}
                                {c.urgency_flag === 'CLOSED' && <span className="text-[10px] px-2 py-0.5 bg-neutral-500/15 text-neutral-400 rounded border border-neutral-500/20">CLOSED</span>}
                                {c.is_duplicate && <span className="text-[10px] px-2 py-0.5 bg-yellow-500/10 text-yellow-400 rounded border border-yellow-500/20">ALREADY EXISTS</span>}
                                {c.unverified && (c.verification_flags || []).map((f, fi) => (
                                  <span key={fi} data-testid="candidate-unverified-flag" className="text-[10px] px-2 py-0.5 bg-orange-500/15 text-orange-400 rounded border border-orange-500/30 font-medium" title={f}>⚠ {f.replace('unverified: ', '')}</span>
                                ))}
                              </div>
                              {c.fit_rationale && <p className="text-xs text-[#00D4C8]/80 mt-1 italic">{c.fit_rationale}</p>}
                              {c.mandate_summary && <p className="text-xs text-[#A3B5C7] mt-1">{c.mandate_summary}</p>}
                              <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                                {c.amount_notes && <span className="text-[#00D4C8]">{c.amount_notes}</span>}
                                {!c.amount_notes && c.amount_max && <span className="text-[#00D4C8]">{c.amount_currency || 'USD'} {Number(c.amount_max).toLocaleString()}</span>}
                                {c.deadline && <span className="text-[#A3B5C7] flex items-center gap-1"><Calendar className="w-3 h-3" />{c.deadline}</span>}
                                {c.deadline_notes && !c.deadline && <span className="text-[#A3B5C7]">{c.deadline_notes}</span>}
                                {c.application_url && <a href={c.application_url} target="_blank" rel="noreferrer" className="text-[#00D4C8] hover:underline flex items-center gap-1" data-testid="opportunity-apply-link">Apply ↗</a>}
                                {(c.thematic_focus || []).slice(0, 3).map(t => (
                                  <span key={t} className="px-1.5 py-0.5 text-[10px] bg-[#00D4C8]/5 text-[#00D4C8]/60 rounded">{t}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {scanData?.status === 'error' && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              Scan failed: {scanData.error}
            </div>
          )}
        </div>

        {isReview && (
          <div className="p-6 border-t border-[#1A2F4C] flex items-center justify-between">
            <span className="text-sm text-[#A3B5C7]">{approvedCount} selected</span>
            <div className="flex gap-3">
              <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
              <button
                onClick={() => {
                  const toSave = candidates.filter((_, i) => approved[i]);
                  onSaveApproved(toSave);
                }}
                className="btn-primary text-sm"
                disabled={approvedCount === 0}
                data-testid="save-scan-results-btn"
              >
                Save {approvedCount} Opportunities
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// ---- Brief Modal ----
const BriefModal = ({ open, brief, loading, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#1A2F4C]">
          <h2 className="text-xl font-bold text-white">Application Brief</h2>
          <button onClick={onClose} className="text-[#A3B5C7] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-[#00D4C8]" />
              <span className="text-[#A3B5C7]">Generating application brief...</span>
            </div>
          ) : brief?.sections ? (
            <div className="space-y-5">
              {brief.sections.map((s, i) => (
                <div key={i}>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-[#00D4C8] mb-2">{s.title}</h3>
                  <p className="text-sm text-[#D4E2F0] leading-relaxed whitespace-pre-wrap">{s.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#A3B5C7]">No brief data available.</p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// ---- Migration Banner ----
const MigrationBanner = ({ candidates, onMigrate, migrating }) => {
  const [expanded, setExpanded] = useState(false);
  if (!candidates?.length) return null;

  return (
    <div className="mb-6 p-4 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid="migration-banner">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          <span className="text-sm text-amber-300">
            <strong>{candidates.length}</strong> existing FINANCING target{candidates.length > 1 ? 's' : ''} could be migrated to the Grants database
          </span>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-amber-400 hover:text-amber-300 text-xs underline">
          {expanded ? 'Hide' : 'Review'}
        </button>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mt-3 space-y-2">
            {candidates.map(c => (
              <div key={c.target_id} className="flex items-center justify-between p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                <div>
                  <span className="text-white text-sm">{c.name}</span>
                  {c.mandate_summary && <p className="text-xs text-[#A3B5C7] mt-1">{c.mandate_summary?.substring(0, 120)}...</p>}
                </div>
                <button
                  onClick={() => onMigrate(c.target_id)}
                  disabled={migrating === c.target_id}
                  className="btn-primary text-xs flex items-center gap-1.5"
                  data-testid={`migrate-btn-${c.target_id}`}
                >
                  {migrating === c.target_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                  Migrate
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---- Main Page ----
const GrantsPage = () => {
  usePageTitle('Funding Intelligence');
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [geoFilter, setGeoFilter] = useState('');
  const [themeFilter, setThemeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editGrant, setEditGrant] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  // Scan state
  const [scanActive, setScanActive] = useState(false);
  const [scanData, setScanData] = useState(null);
  const [scanJobId, setScanJobId] = useState(null);
  const [latestScan, setLatestScan] = useState(null);
  // Brief state
  const [briefModal, setBriefModal] = useState(false);
  const [briefData, setBriefData] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  // Migration
  const [migrationCandidates, setMigrationCandidates] = useState([]);
  const [migrating, setMigrating] = useState(null);
  // Add to Pipeline
  const [addingToPipeline, setAddingToPipeline] = useState(null);

  const loadGrants = useCallback(async () => {
    try {
      const res = await grantsApi.getAll();
      setGrants(res.data.grants || []);
    } catch (err) {
      console.error('Failed to load grants:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMigrationCandidates = useCallback(async () => {
    try {
      const res = await grantsApi.getMigrationCandidates();
      setMigrationCandidates(res.data.candidates || []);
    } catch (err) {
      console.error('Migration candidates error:', err);
    }
  }, []);

  const loadLatestScan = useCallback(async () => {
    try {
      const res = await grantsApi.scanLatest();
      setLatestScan(res.data?.exists ? res.data : null);
    } catch (err) {
      console.error('Latest scan load error:', err);
    }
  }, []);

  useEffect(() => { loadGrants(); loadMigrationCandidates(); loadLatestScan(); }, [loadGrants, loadMigrationCandidates, loadLatestScan]);

  // Filter logic
  const filtered = grants.filter(g => {
    if (statusFilter === 'Open') return ['OPEN', 'OPEN_ACT_SOON'].includes(g.urgency) && !['Closed', 'Missed'].includes(g.status);
    if (statusFilter === 'Closing Soon') return ['URGENT', 'CLOSING_SOON'].includes(g.urgency);
    if (statusFilter === 'Applied') return ['Applying', 'Submitted'].includes(g.status);
    if (statusFilter === 'Awarded') return g.status === 'Awarded';
    if (statusFilter === 'Closed') return g.urgency === 'CLOSED' || g.status === 'Missed';
    return true;
  }).filter(g => {
    if (geoFilter && !(g.geographic_focus || []).some(f => f.toLowerCase().includes(geoFilter.toLowerCase()))) return false;
    if (themeFilter && !(g.thematic_focus || []).some(f => f.toLowerCase().includes(themeFilter.toLowerCase()))) return false;
    return true;
  });

  // Handlers
  const handleSaveGrant = async (data) => {
    try {
      if (editGrant) {
        await grantsApi.update(editGrant.id, data);
        toast.success('Grant updated');
      } else {
        await grantsApi.create(data);
        toast.success('Grant added');
      }
      setShowModal(false);
      setEditGrant(null);
      loadGrants();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this grant?')) return;
    try {
      await grantsApi.delete(id);
      toast.success('Grant deleted');
      loadGrants();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await grantsApi.updateStatus(id, status);
      setGrants(prev => prev.map(g => g.id === id ? {...g, status} : g));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleStartScan = async () => {
    try {
      const res = await grantsApi.startScan();
      setScanJobId(res.data.job_id);
      setScanData(res.data);
      setScanActive(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleViewLastScan = () => {
    if (!latestScan) return;
    setScanJobId(null);
    setScanData({
      status: 'review',
      cached: true,
      scanned_at: latestScan.scanned_at,
      candidates: latestScan.candidates || [],
      top_3_actions: latestScan.top_3_actions || [],
      searches_done: 0,
      total_searches: 0,
    });
    setScanActive(true);
  };

  // Poll scan status
  useEffect(() => {
    if (!scanJobId || !scanActive) return;
    let cancelled = false;
    pollJob({
      fetchStatus: () => grantsApi.scanStatus(scanJobId).then(r => r.data),
      isComplete: (d) => ['review', 'complete'].includes(d.status),
      isError: (d) => d.status === 'error' || d.status === 'expired',
      getError: (d) => d.error || 'The scan failed. Please try again.',
      onProgress: (d) => { if (!cancelled) setScanData(d); },
      onComplete: (d) => { if (!cancelled) { setScanData(d); loadLatestScan(); } },
      onError: (msg) => {
        if (cancelled) return;
        setScanData((prev) => ({ ...(prev || {}), status: 'error', error: msg }));
        toast.error(msg);
      },
      intervalMs: 2000,
    });
    return () => { cancelled = true; };
  }, [scanJobId, scanActive]);

  const handleSaveScanResults = async (approved) => {
    try {
      await grantsApi.saveScanResults(approved);
      toast.success(`${approved.length} grants saved`);
      setScanActive(false);
      setScanJobId(null);
      loadGrants();
      loadLatestScan();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleGenerateBrief = async (grantId) => {
    setBriefModal(true);
    setBriefLoading(true);
    setBriefData(null);
    try {
      const res = await grantsApi.generateBrief(grantId);
      const jobId = res.data.job_id;
      await pollJob({
        fetchStatus: () => grantsApi.briefStatus(jobId).then(r => r.data),
        isComplete: (d) => d.status === 'complete',
        isError: (d) => d.status === 'error' || d.status === 'expired',
        getError: (d) => d.error || 'Brief generation failed',
        onComplete: (d) => { setBriefData(d.brief); setBriefLoading(false); },
        onError: (msg) => { toast.error(msg); setBriefLoading(false); },
        intervalMs: 3000,
      });
    } catch (err) {
      toast.error(formatApiError(err));
      setBriefLoading(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await grantsApi.exportCsv();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `grants_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleMigrate = async (targetId) => {
    setMigrating(targetId);
    try {
      await grantsApi.migrate(targetId);
      toast.success('Target migrated to Grants');
      setMigrationCandidates(prev => prev.filter(c => c.target_id !== targetId));
      loadGrants();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMigrating(null);
    }
  };

  const handleAddToPipeline = async (grant) => {
    setAddingToPipeline(grant.id);
    try {
      const res = await grantsApi.addToPipeline(grant.id);
      const stageCategory = res.data.stage_category || 'Funding & Grants';
      toast.success(`Added to Pipeline — ${grant.name} · ${stageCategory} · Identified`);
      setGrants(prev => prev.map(g =>
        g.id === grant.id ? { ...g, pipeline_record_id: res.data.pipeline_record_id } : g
      ));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingToPipeline(null);
    }
  };

  const formatAmount = (g) => {
    if (!g.amount_min && !g.amount_max) return null;
    const cur = g.amount_currency || 'USD';
    const sym = cur === 'GBP' ? '£' : cur === 'EUR' ? '€' : '$';
    if (g.amount_min && g.amount_max) return `${sym}${Number(g.amount_min).toLocaleString()} – ${sym}${Number(g.amount_max).toLocaleString()} ${cur}`;
    if (g.amount_max) return `Up to ${sym}${Number(g.amount_max).toLocaleString()} ${cur}`;
    if (g.amount_min) return `From ${sym}${Number(g.amount_min).toLocaleString()} ${cur}`;
    return null;
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-[#00D4C8]" />
    </div>
  );

  const urg = (u) => URGENCY_CONFIG[u] || URGENCY_CONFIG.UNKNOWN;

  return (
    <div className="space-y-6" data-testid="grants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">Funding Intelligence</h1>
          <HowDoesThisWorkPill feature="grants" />
          <p className="text-sm text-[#A3B5C7] mt-1">Contextually filtered opportunity scan matched against your Opportunity Universe</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => { setEditGrant(null); setShowModal(true); }} className="btn-primary text-sm flex items-center gap-2" data-testid="add-grant-btn">
            <Plus className="w-4 h-4" /> Add Grant
          </button>
          {latestScan && (
            <button onClick={handleViewLastScan} className="btn-secondary text-sm flex items-center gap-2" data-testid="view-last-scan-btn" title={latestScan.scanned_at ? `Scanned ${new Date(latestScan.scanned_at).toLocaleString()}` : ''}>
              <Radar className="w-4 h-4" /> Last scan{typeof latestScan.age_days === 'number' ? ` · ${latestScan.age_days === 0 ? 'today' : `${latestScan.age_days}d ago`}` : ''} ({(latestScan.candidates || []).length})
              {latestScan.stale && <span className="text-amber-400 text-xs">· stale</span>}
            </button>
          )}
          <button onClick={handleStartScan} className="btn-primary text-sm flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 border-emerald-500" data-testid="scan-grants-btn">
            <Radar className="w-4 h-4" /> {latestScan ? 'Refresh Funding Intelligence' : 'Funding Intelligence Scan'}
          </button>
          <button onClick={handleExportCsv} className="btn-secondary text-sm flex items-center gap-2" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <HelpButton pageId="grants" />
        </div>
      </div>

      {/* Migration Banner */}
      <MigrationBanner candidates={migrationCandidates} onMigrate={handleMigrate} migrating={migrating} />

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3" data-testid="grants-filter-bar">
        {/* Status chips */}
        {STATUS_FILTERS.map(f => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all ${
              statusFilter === f
                ? 'bg-[#00D4C8]/15 text-[#00D4C8] border-[#00D4C8]/30'
                : 'bg-transparent text-[#A3B5C7] border-[#1A2F4C] hover:border-[#A3B5C7]/40'
            }`}
            data-testid={`filter-${f.toLowerCase().replace(' ', '-')}`}
          >
            {f} {f !== 'All' && `(${grants.filter(g => {
              if (f === 'Open') return ['OPEN', 'OPEN_ACT_SOON'].includes(g.urgency) && !['Closed', 'Missed'].includes(g.status);
              if (f === 'Closing Soon') return ['URGENT', 'CLOSING_SOON'].includes(g.urgency);
              if (f === 'Applied') return ['Applying', 'Submitted'].includes(g.status);
              if (f === 'Awarded') return g.status === 'Awarded';
              if (f === 'Closed') return g.urgency === 'CLOSED' || g.status === 'Missed';
              return true;
            }).length})`}
          </button>
        ))}
        {/* Dropdown filters */}
        <div className="flex items-center gap-2 ml-auto">
          <select
            value={geoFilter}
            onChange={e => setGeoFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-xs text-[#A3B5C7] focus:border-[#00D4C8]"
            data-testid="geo-filter"
          >
            <option value="">All Regions</option>
            {[...new Set(grants.flatMap(g => g.geographic_focus || []))].sort().map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <select
            value={themeFilter}
            onChange={e => setThemeFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-xs text-[#A3B5C7] focus:border-[#00D4C8]"
            data-testid="theme-filter"
          >
            <option value="">All Themes</option>
            {[...new Set(grants.flatMap(g => g.thematic_focus || []))].sort().map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Grant Cards */}
      <div className="space-y-4" data-testid="grants-list">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <DollarSign className="w-12 h-12 text-[#A3B5C7]/30 mx-auto mb-4" />
            <p className="text-[#A3B5C7] text-sm">No grants found. Add one manually or run a scan.</p>
          </div>
        ) : (
          filtered.map(g => {
            const u = urg(g.urgency);
            const amount = formatAmount(g);
            const isExpanded = expandedCard === g.id;

            return (
              <motion.div
                key={g.id}
                layout
                className="card-surface overflow-hidden"
                data-testid={`grant-card-${g.id}`}
              >
                {/* Main card row */}
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Urgency badge */}
                    <div className={`flex-shrink-0 px-2.5 py-1 rounded text-[10px] uppercase tracking-wider font-bold ${u.bg} ${u.text} border ${u.border}`}>
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${u.dot}`} />
                        {u.label}
                      </div>
                    </div>

                    {/* In Pipeline badge */}
                    {g.pipeline_record_id && (
                      <div className="flex-shrink-0 px-2.5 py-1 rounded text-[10px] uppercase tracking-wider font-bold bg-[#00D4C8]/10 text-[#00D4C8] border border-[#00D4C8]/25" data-testid={`in-pipeline-badge-${g.id}`}>
                        <div className="flex items-center gap-1.5">
                          <GitBranch className="w-3 h-3" />
                          In Pipeline
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-white font-semibold text-base">{g.name}</h3>
                          <div className="flex items-center gap-3 mt-1 text-xs text-[#A3B5C7]">
                            {g.funder_name && <span>{g.funder_name}</span>}
                            {amount && <span className="text-[#00D4C8] font-medium">{amount}</span>}
                            {g.fit_score && (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                g.fit_score >= 8 ? 'bg-emerald-500/15 text-emerald-400' :
                                g.fit_score >= 5 ? 'bg-amber-500/15 text-amber-400' :
                                'bg-neutral-500/15 text-neutral-400'
                              }`}>FIT {g.fit_score}/10</span>
                            )}
                          </div>
                          {g.fit_rationale && <p className="text-[11px] text-[#00D4C8]/70 mt-0.5 italic">{g.fit_rationale}</p>}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {g.deadline && (
                            <span className="text-xs text-[#A3B5C7] flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {g.deadline}
                              {g.days_remaining != null && g.days_remaining > 0 && (
                                <span className={`ml-1 font-medium ${g.days_remaining <= 14 ? 'text-red-400' : g.days_remaining <= 30 ? 'text-orange-400' : 'text-emerald-400'}`}>
                                  ({g.days_remaining}d)
                                </span>
                              )}
                            </span>
                          )}
                          {g.application_url && (
                            <a href={g.application_url} target="_blank" rel="noopener noreferrer" className="text-[#00D4C8] hover:text-[#33DDD3]">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {g.opportunity_type && <span className="px-2 py-0.5 text-[10px] bg-purple-500/10 text-purple-400 rounded border border-purple-500/15 font-medium">{g.opportunity_type}</span>}
                        {g.geographic_eligibility && <span className="px-2 py-0.5 text-[10px] bg-blue-500/10 text-blue-400/80 rounded border border-blue-500/15">{g.geographic_eligibility}</span>}
                        {g.type && !g.opportunity_type && <span className="px-2 py-0.5 text-[10px] bg-white/5 text-[#A3B5C7] rounded border border-[#1A2F4C]">{g.type}</span>}
                        {(g.thematic_focus || []).map(t => (
                          <span key={t} className="px-2 py-0.5 text-[10px] bg-[#00D4C8]/5 text-[#00D4C8]/70 rounded border border-[#00D4C8]/10">{t}</span>
                        ))}
                        {(g.geographic_focus || []).map(gf => (
                          <span key={gf} className="px-2 py-0.5 text-[10px] bg-blue-500/5 text-blue-400/70 rounded border border-blue-500/10">{gf}</span>
                        ))}
                      </div>

                      {/* Mandate summary */}
                      {g.mandate_summary && (
                        <p className="text-xs text-[#D4E2F0]/70 mt-2 line-clamp-2">{g.mandate_summary}</p>
                      )}

                      {/* Relevant projects */}
                      {(g.relevant_projects || []).length > 0 && (
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          <Star className="w-3 h-3 text-amber-400" />
                          {g.relevant_projects.map((p, pi) => (
                            <span key={pi} className="text-amber-400/80">
                              {typeof p === 'string' ? p : p.name || p.project}
                              {p.relevance && ` (${p.relevance}/5)`}
                              {pi < g.relevant_projects.length - 1 && ','}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Programme officer */}
                      {g.programme_officer_name && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-[#A3B5C7]">
                          <User className="w-3 h-3" />
                          <span>{g.programme_officer_name}</span>
                          {g.programme_officer_email && (
                            <a href={`mailto:${g.programme_officer_email}`} className="text-[#00D4C8] hover:underline flex items-center gap-1">
                              <Mail className="w-3 h-3" />{g.programme_officer_email}
                            </a>
                          )}
                        </div>
                      )}

                      {/* Action row */}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1A2F4C]">
                        {/* Status selector */}
                        <select
                          value={g.status || 'Watching'}
                          onChange={e => handleStatusChange(g.id, e.target.value)}
                          className="px-3 py-1 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-xs text-[#D4E2F0] focus:border-[#00D4C8]"
                          data-testid={`status-select-${g.id}`}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>

                        <div className="flex items-center gap-2">
                          <button onClick={() => setExpandedCard(isExpanded ? null : g.id)} className="btn-secondary text-xs px-3 py-1 flex items-center gap-1.5" data-testid={`view-card-${g.id}`}>
                            <Eye className="w-3 h-3" /> {isExpanded ? 'Collapse' : 'View Full Card'}
                          </button>
                          <button onClick={() => handleGenerateBrief(g.id)} className="btn-secondary text-xs px-3 py-1 flex items-center gap-1.5" data-testid={`gen-brief-${g.id}`}>
                            <BookOpen className="w-3 h-3" /> Generate Brief
                          </button>
                          {g.pipeline_record_id ? (
                            <span className="text-[10px] px-3 py-1 rounded-lg bg-[#00D4C8]/5 text-[#00D4C8]/60 border border-[#00D4C8]/15 flex items-center gap-1.5" data-testid={`pipeline-linked-${g.id}`}>
                              <Check className="w-3 h-3" /> Pipeline
                            </span>
                          ) : (
                            <button
                              onClick={() => handleAddToPipeline(g)}
                              disabled={addingToPipeline === g.id}
                              className="btn-primary text-xs px-3 py-1 flex items-center gap-1.5"
                              data-testid={`add-to-pipeline-${g.id}`}
                            >
                              {addingToPipeline === g.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitBranch className="w-3 h-3" />}
                              Add to Pipeline
                            </button>
                          )}
                          <button onClick={() => { setEditGrant(g); setShowModal(true); }} className="btn-secondary text-xs p-1.5" data-testid={`edit-grant-${g.id}`}>
                            <Edit3 className="w-3 h-3" />
                          </button>
                          <button onClick={() => handleDelete(g.id)} className="text-red-400/50 hover:text-red-400 p-1.5" data-testid={`delete-grant-${g.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded detail view */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden border-t border-[#1A2F4C]"
                    >
                      <div className="p-5 bg-[#0A1628] space-y-3 text-sm">
                        {g.eligibility_criteria && (
                          <div><span className="text-xs uppercase text-[#A3B5C7]">Eligibility:</span><p className="text-[#D4E2F0] mt-1">{g.eligibility_criteria}</p></div>
                        )}
                        {g.amount_notes && (
                          <div><span className="text-xs uppercase text-[#A3B5C7]">Amount Notes:</span><p className="text-[#D4E2F0] mt-1">{g.amount_notes}</p></div>
                        )}
                        {(g.past_grantees || []).length > 0 && (
                          <div><span className="text-xs uppercase text-[#A3B5C7]">Past Grantees:</span><p className="text-[#D4E2F0] mt-1">{g.past_grantees.join(', ')}</p></div>
                        )}
                        {g.notes && (
                          <div><span className="text-xs uppercase text-[#A3B5C7]">Notes:</span><p className="text-[#D4E2F0] mt-1">{g.notes}</p></div>
                        )}
                        {g.source && (
                          <div className="text-xs text-[#A3B5C7]">Source: {g.source}</div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Modals */}
      <AddGrantModal open={showModal} onClose={() => { setShowModal(false); setEditGrant(null); }} onSave={handleSaveGrant} editGrant={editGrant} />
      <ScanProgressModal open={scanActive} scanData={scanData} onClose={() => { setScanActive(false); setScanJobId(null); }} onSaveApproved={handleSaveScanResults} />
      <BriefModal open={briefModal} brief={briefData} loading={briefLoading} onClose={() => setBriefModal(false)} />
    </div>
  );
};

export default GrantsPage;
